import { ConvexError } from "convex/values";
import { type AppErrorData, ERROR_CODES } from "@shared/errors/app-error";
import { captureError, type CaptureErrorContext } from "@/lib/error-tracking";
import { isGoogleMapsRpcTransportNoiseValue } from "@/lib/error-tracking/google-maps-noise";
import { captureShownError } from "@/lib/error-tracking/shown-error";
import { showGlobalError } from "@/lib/feedback/global-error-event";
import type { ReportIssueContext } from "@/lib/feedback/types";
import { DocumentGenerationError } from "@/lib/documents/generate-document-client";
import { readUnknownProperty, toUnknownRecord } from "@shared/validation/narrow";

export type ErrorRecovery = "none" | "retry" | "wait" | "sign-in";

interface HandledErrorFields {
  code: string;
  recovery: ErrorRecovery;
  reportable: boolean;
  errorEventId?: string;
}

export type HandledErrorResult =
  | (HandledErrorFields & { action: "ignore"; message: null })
  | (HandledErrorFields & { action: "notify"; message: string });

export interface HandleErrorOptions {
  fallbackMessage?: string;
  reportContext?: CaptureErrorContext | Record<string, unknown>;
  display?: "silent" | "banner";
  reportIssueContext?: ReportIssueContext;
}

export const CONNECTION_LOST_CODE = "connection_lost";
export const NETWORK_UNREACHABLE_CODE = "network_unreachable";
export const FILE_UNREADABLE_CODE = "file_unreadable";
const NETWORK_FAILURE_MESSAGES = new Set([
  "failed to fetch",
  "load failed",
  "network error",
  "networkerror when attempting to fetch resource.",
]);
const UNREADABLE_FILE_ERROR_NAMES = new Set(["NotReadableError"]);
const CONNECTION_LOST_MESSAGE = /^(connection lost while action was in flight|network connection lost\.?)$/i;

const CONNECTION_LOST_RESULT: HandledErrorResult = {
  action: "notify",
  code: CONNECTION_LOST_CODE,
  message: "Your connection dropped. Refresh the page to check whether it saved.",
  recovery: "retry",
  reportable: false,
};

function isConnectionLost(error: unknown): boolean {
  const message = readUnknownProperty(error, "message");
  return typeof message === "string" && CONNECTION_LOST_MESSAGE.test(message.trim());
}

function describeRetryWait(retryAfterMs: unknown): string {
  if (typeof retryAfterMs !== "number" || !Number.isFinite(retryAfterMs)) {
    return "Try again shortly.";
  }
  const minutes = Math.ceil(retryAfterMs / 60_000);
  if (minutes <= 1) return "Try again in under a minute.";
  if (minutes < 60) return `Try again in ${minutes} minutes.`;
  const hours = Math.round(minutes / 60);
  return hours === 1
    ? "Try again in about an hour."
    : `Try again in about ${hours} hours.`;
}

const RECOVERY_BY_CODE: Partial<Record<string, ErrorRecovery>> = {
  [ERROR_CODES.AUTH_REQUIRED]: "sign-in",
  [ERROR_CODES.RATE_LIMIT]: "wait",
  [ERROR_CODES.DEPENDENCY_FAILED]: "retry",
};

function readAppData(data: unknown): AppErrorData {
  if (typeof data === "string") {
    return { code: ERROR_CODES.UNKNOWN, message: data };
  }
  const rec = toUnknownRecord(data);
  if (rec.kind === "RateLimited") {
    return {
      code: ERROR_CODES.RATE_LIMIT,
      message: `You've hit a rate limit. ${describeRetryWait(rec.retryAfter)}`,
    };
  }
  return {
    code: typeof rec.code === "string" ? rec.code : ERROR_CODES.UNKNOWN,
    message:
      typeof rec.message === "string"
        ? rec.message
        : "Something went wrong. Please try again.",
  };
}

function isNetworkFailure(error: unknown): boolean {
  if (error instanceof TypeError) {
    return NETWORK_FAILURE_MESSAGES.has(error.message.trim().toLowerCase());
  }
  return (
    error instanceof Error && isGoogleMapsRpcTransportNoiseValue(error.message)
  );
}

function isUnreadableFile(error: unknown): boolean {
  const name = readUnknownProperty(error, "name");
  return (
    error instanceof Error &&
    typeof name === "string" &&
    UNREADABLE_FILE_ERROR_NAMES.has(name)
  );
}

function resolveKnownClientError(error: unknown): HandledErrorResult | null {
  if (isUnreadableFile(error)) {
    return {
      action: "notify",
      code: FILE_UNREADABLE_CODE,
      message: "We couldn't read that file. Choose it again, or save a copy to this device first.",
      recovery: "none",
      reportable: false,
    };
  }
  if (isNetworkFailure(error)) {
    return {
      action: "notify",
      code: NETWORK_UNREACHABLE_CODE,
      message: "We couldn't reach Repackify. Check your connection and try again.",
      recovery: "retry",
      reportable: false,
    };
  }
  if (readUnknownProperty(error, "name") === "PhotoValidationError" && error instanceof Error) {
    return {
      action: "notify",
      code: "photo_validation",
      message: error.message,
      recovery: "none",
      reportable: false,
    };
  }
  if (error instanceof DocumentGenerationError) {
    return {
      action: "notify",
      code: "document_generation",
      message: error.message,
      recovery: error.reportable ? "retry" : "none",
      reportable: error.reportable,
    };
  }
  return null;
}

export function resolveHandledError(
  error: unknown,
  fallbackMessage = "Something went wrong. Please try again.",
): HandledErrorResult {
  if (readUnknownProperty(error, "name") === "AbortError") {
    return {
      action: "ignore",
      code: "aborted",
      message: null,
      recovery: "none",
      reportable: false,
    };
  }

  if (!(error instanceof ConvexError) && isConnectionLost(error)) {
    return { ...CONNECTION_LOST_RESULT };
  }

  if (error instanceof ConvexError) {
    const data = readAppData(error.data);
    return {
      action: "notify",
      code: data.code,
      message: data.message,
      recovery: RECOVERY_BY_CODE[data.code] ?? "none",
      reportable: false,
    };
  }

  return (
    resolveKnownClientError(error) ?? {
      action: "notify",
      code: ERROR_CODES.UNKNOWN,
      message: fallbackMessage,
      recovery: "retry",
      reportable: true,
    }
  );
}

export function describeError(error: unknown, fallbackMessage: string): string {
  return resolveHandledError(error, fallbackMessage).message ?? fallbackMessage;
}

export function handleError(
  error: unknown,
  options: HandleErrorOptions = {},
): HandledErrorResult {
  const {
    fallbackMessage,
    reportContext,
    display = "banner",
    reportIssueContext,
  } = options;

  const result = resolveHandledError(error, fallbackMessage);

  if (result.action === "ignore") return result;

  captureShownError({
    code: result.code,
    message: result.message,
    reportable: result.reportable,
    display,
  });

  if (result.reportable) {
    result.errorEventId = captureError(error, reportContext);
  }

  if (display === "banner" && result.message) {
    showGlobalError({
      message: result.message,
      code: result.code,
      reportable: result.reportable,
      reportContext: {
        ...(reportIssueContext ?? {}),
        ...(result.errorEventId
          ? { errorEventId: result.errorEventId }
          : {}),
      },
    });
  }

  return result;
}
